/** Interaction signals supplement server enforcement; timers are never authority. */
export interface SessionStatus {
  session_id: string;
  server_time: string;
  idle_expires_at: string;
  absolute_expires_at: string;
}

export class SessionExpiredError extends Error {}
class SessionVerificationTimeout extends Error {}

export function watchSessionActivity(options: {
  getStatus: () => Promise<SessionStatus>;
  signalActivity: () => Promise<SessionStatus>;
  onExpired: () => void;
  onUnavailable: () => void;
}): () => void {
  let ended = false, ready = false, busy = true;
  let deadline = 0, absoluteServer = Infinity, lastServer = -Infinity;
  let staleChecks = 0, sessionId: string | undefined;
  let lastSignal = -Infinity;
  let probing = false;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  const events = ["pointerdown", "keydown", "touchstart", "wheel"] as const;

  function finish(unavailable = false) {
    if (ended) return;
    ended = true;
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    if (unavailable) options.onUnavailable(); else options.onExpired();
  }
  async function bounded<T>(request: () => Promise<T>): Promise<T> {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([request(), new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new SessionVerificationTimeout("Session verification timed out")), 10_000);
      })]);
    } finally { if (timeout !== undefined) clearTimeout(timeout); }
  }
  async function confirmExpiry() {
    if (ended || probing) return;
    probing = true;
    const started = Date.now();
    try { schedule(await bounded(options.getStatus), started); }
    catch (error) { finish(!(error instanceof SessionExpiredError)); }
    finally { probing = false; }
  }
  function checkExpiry() {
    if (ended) return;
    if (Date.now() >= deadline) { void confirmExpiry(); return; }
    expiryTimer = setTimeout(checkExpiry, Math.min(deadline - Date.now(), 2_147_483_647));
  }
  function schedule(status: SessionStatus, started: number) {
    if (ended) return;
    const server = Date.parse(status.server_time), idle = Date.parse(status.idle_expires_at), absolute = Date.parse(status.absolute_expires_at);
    if (![server, idle, absolute].every(Number.isFinite) || idle > absolute || (sessionId && sessionId !== status.session_id)) {
      finish(true); return;
    }
    sessionId = status.session_id;
    // Use request start rather than response arrival, so network delay cannot
    // extend the local estimate. Only the server decides actual validity.
    if (server < lastServer) return; // Ignore an older response arriving after a newer one.
    lastServer = server;
    absoluteServer = Math.min(absoluteServer, absolute);
    deadline = started + Math.min(idle, absoluteServer) - server;
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    if (deadline <= Date.now()) {
      // A valid response may have aged in transit while another tab extended
      // this session. Confirm again; a local estimate never revokes a session.
      ready = false;
      if (++staleChecks >= 3) { finish(true); return; }
      expiryTimer = setTimeout(() => { void confirmExpiry(); }, 0);
      return;
    }
    staleChecks = 0; ready = true;
    checkExpiry();
  }
  async function signal() {
    busy = true; lastSignal = Date.now();
    const started = Date.now();
    // An activity ping can queue behind workspace reads. Its timeout is not
    // proof of expiry and must neither discard nor extend an existing verified
    // deadline. Keep observing the real response: late denial still ends this
    // view immediately, and only a validated server response may reschedule it.
    const pending = options.signalActivity();
    void pending.then(status => schedule(status, started), error => finish(!(error instanceof SessionExpiredError)));
    try { await bounded(() => pending); }
    catch (error) {
      if (!(error instanceof SessionVerificationTimeout && ready && Date.now() < deadline)) {
        finish(!(error instanceof SessionExpiredError));
      }
    }
    finally { busy = false; }
  }
  function interact(event: Event) {
    if (ended || !ready || document.visibilityState !== "visible" || !event.isTrusted) return;
    if (Date.now() >= deadline) { void confirmExpiry(); return; }
    // Leading debounce: ongoing genuine interaction can signal every 15 seconds;
    // no trailing timer or polling loop can turn silence into activity.
    if (!busy && Date.now() - lastSignal >= 15_000) void signal();
  }
  function visible() {
    if (!ended && ready && document.visibilityState === "visible" && Date.now() >= deadline) void confirmExpiry();
  }
  for (const event of events) document.addEventListener(event, interact, { passive: true });
  document.addEventListener("visibilitychange", visible);
  const started = Date.now();
  void bounded(options.getStatus).then(status => schedule(status, started), error => finish(!(error instanceof SessionExpiredError))).finally(() => { busy = false; });
  return () => {
    ended = true;
    if (expiryTimer !== undefined) clearTimeout(expiryTimer);
    for (const event of events) document.removeEventListener(event, interact);
    document.removeEventListener("visibilitychange", visible);
  };
}
